import React, { useState, useEffect } from 'react';
import { FaTimes, FaTag, FaSave, FaSpinner, FaInfoCircle } from 'react-icons/fa';

interface Designation {
  id: number;
  name: string;
  is_active: boolean;
  show_in_reporting_person_list?: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by?: number;
  suspended_by?: number;
  suspended_at?: string;
}

interface DesignationFormData {
  name: string;
  is_active: boolean;
  show_in_reporting_person_list: boolean;
}

interface EditDesignationFormData extends DesignationFormData {
  id: number;
}

// Format date
export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

interface EditDesignationOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EditDesignationFormData) => void;
  designation: Designation | null;
  loading?: boolean;
}

export const EditDesignationOffcanvas: React.FC<EditDesignationOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  designation,
  loading = false
}) => {
  const [formData, setFormData] = useState<DesignationFormData>({
    name: '',
    is_active: true,
    show_in_reporting_person_list: false
  });

  const [errors, setErrors] = useState<Partial<DesignationFormData>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof DesignationFormData, boolean>>>({});

  // Populate form when designation changes
  useEffect(() => {
    if (designation && isOpen) {
      setFormData({
        name: designation.name || '',
        is_active: designation.is_active,
        show_in_reporting_person_list: Boolean(designation.show_in_reporting_person_list)
      });
      setErrors({});
      setTouched({});
    }
  }, [designation, isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setTouched({});
    }
  }, [isOpen]);

  // Validation
  const validateField = (name: keyof DesignationFormData, value: string) => {
    switch (name) {
      case 'name':
        if (!value.trim()) {
          return 'Designation name is required';
        }
        if (value.trim().length < 2) {
          return 'Designation name must be at least 2 characters';
        }
        if (value.trim().length > 100) {
          return 'Designation name cannot exceed 100 characters';
        }
        return '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const fieldKey = key as keyof DesignationFormData;
      if (fieldKey === 'name') {
        const error = validateField(fieldKey, formData[fieldKey] as string);
        if (error) {
          newErrors[fieldKey] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof DesignationFormData;

    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Handle blur events for validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof DesignationFormData;

    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));

    const error = validateField(fieldName, value);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!designation) return;

    // Mark all fields as touched
    setTouched({
      name: true,
      is_active: true,
      show_in_reporting_person_list: true
    });

    if (validateForm()) {
      onSubmit({
        ...formData,
        id: designation.id
      });
    }
  };

  // Check if form has changes
  const hasChanges = designation ? (
    formData.name !== designation.name ||
    formData.is_active !== designation.is_active ||
    formData.show_in_reporting_person_list !== Boolean(designation.show_in_reporting_person_list)
  ) : false;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen || !designation) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={!loading ? onClose : undefined}
      />

      {/* Offcanvas */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FaTag className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Edit Designation</h2>
              <p className="text-sm text-gray-600">Last updated: {formatDate(designation.updated_at)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            disabled={loading}
          >
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-6">
            {/* Designation Info */}
            <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
              <h4 className="text-sm font-medium text-gray-800 mb-2">Designation Information</h4>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-gray-500">ID:</span>
                  <p className="font-medium">{designation.id}</p>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <p className="font-medium">{formatDate(designation.created_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated:</span>
                  <p className="font-medium">{formatDate(designation.updated_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Current Status:</span>
                  <p className={`font-medium ${designation.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {designation.is_active ? 'Active' : 'Suspended'}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Designation Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Designation Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.name && touched.name
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                    } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="Enter designation name"
                  maxLength={100}
                />
                {errors.name && touched.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.name.length}/100 characters
                </p>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.is_active ? 'ACTIVE' : 'SUSPENDED'}
                  onChange={handleInputChange}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="SUSPENDED">Suspended</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Change the designation status
                </p>
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_in_reporting_person_list}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        show_in_reporting_person_list: e.target.checked
                      }));
                      setTouched(prev => ({
                        ...prev,
                        show_in_reporting_person_list: true
                      }));
                    }}
                    disabled={loading}
                    className="h-4 w-4 rounded border-gray-300 text-blue-700 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Allow in Reporting Person List
                  </span>
                </label>
              </div>

              {/* Warning for status change */}
              {formData.is_active !== designation?.is_active && designation.is_active && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded">
                  <FaInfoCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Status Change Warning</p>
                    <p className="text-xs mt-1">
                      Suspending this designation may affect any associated records or users.
                      Make sure this change is intended.
                    </p>
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                <div className="flex items-start gap-3">
                  <FaTag className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Edit Guidelines</p>
                    <ul className="text-xs space-y-1 list-disc list-inside">
                      <li>Ensure the designation name remains professional</li>
                      <li>Consider the impact of status changes</li>
                      <li>Save changes only when you're certain</li>
                      <li>Review all information before submitting</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={loading || !formData.name.trim()}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-950 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FaSpinner className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    Update Designation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
